/**
 * The keyboard model a `role="radiogroup"` owes — Phase 10.
 *
 * `<OutcomeToggle>` and `<SegmentedControl>` both publish `role="radiogroup"`
 * with `role="radio"` children and, until this phase, both implemented exactly
 * none of what that role promises: every option was a plain `<button>`, so all
 * of them sat in the tab order and none of the arrow keys did anything. A
 * screen reader announced "radio, 1 of 2" and then behaved like a toolbar.
 *
 * The rule (WAI-ARIA authoring practices, radio group): **one** stop in the tab
 * order for the whole group, and the arrows move both selection and focus. That
 * is not pedantry on a trading ticket — the group in question is UP versus DOWN,
 * and a keyboard user tabbing through the ticket should reach the size field in
 * one press, not after stepping past an option they did not want.
 *
 * A pure function rather than a hook, so the two components share one model and
 * it can be tested without a DOM (invariant 10's spirit: the decision is
 * separable from the rendering).
 */

/** The keys a radio group must answer to. */
export type RadioKey = 'ArrowRight' | 'ArrowDown' | 'ArrowLeft' | 'ArrowUp' | 'Home' | 'End'

const FORWARD: readonly string[] = ['ArrowRight', 'ArrowDown']
const BACKWARD: readonly string[] = ['ArrowLeft', 'ArrowUp']

/**
 * Where a key press moves to, or `null` if the key is not ours.
 *
 * `enabled` is the per-option disabled mask: a disabled radio is skipped rather
 * than landed on and then bounced off, because landing on it moves selection —
 * in a radio group focus *is* selection, which is the property that makes the
 * skip mandatory rather than cosmetic.
 *
 * Wraps at both ends, which the practices specify and which is also what a
 * two-option group needs to be usable at all: without wrapping, `ArrowRight` on
 * DOWN would do nothing and the user would have to find `ArrowLeft`.
 *
 * Returns `null` when nothing is selectable, so a caller cannot accidentally
 * select index 0 of an all-disabled group.
 */
export function nextRadioIndex(
  key: string,
  current: number,
  count: number,
  enabled: (index: number) => boolean = () => true,
): number | null {
  if (count <= 0) return null

  const firstEnabled = (from: number, step: number): number | null => {
    for (let i = 0; i < count; i++) {
      const at = (((from + i * step) % count) + count) % count
      if (enabled(at)) return at
    }
    return null
  }

  if (key === 'Home') return firstEnabled(0, 1)
  if (key === 'End') return firstEnabled(count - 1, -1)
  if (FORWARD.includes(key)) return firstEnabled(current + 1, 1)
  if (BACKWARD.includes(key)) return firstEnabled(current - 1, -1)
  return null
}

/**
 * The roving `tabIndex` for one option.
 *
 * The selected option is the group's single tab stop. When nothing is selected —
 * which this product never does, but the function has to be total — the first
 * option takes it, so the group is never unreachable by keyboard.
 */
export function radioTabIndex(index: number, selectedIndex: number): 0 | -1 {
  if (selectedIndex < 0) return index === 0 ? 0 : -1
  return index === selectedIndex ? 0 : -1
}
